import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { FeatureFlagAssignmentTargetPanelProps } from '../../../types/features/featureFlags';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import { useFeatureFlagUserOptions } from '../hooks/useFeatureFlagUserOptions';
import FeatureFlagRolloutTargetControl from './FeatureFlagRolloutTargetControl';
import FeatureFlagStoreSelector from './FeatureFlagStoreSelector';
import FeatureFlagUserSelector from './FeatureFlagUserSelector';

const FeatureFlagAssignmentTargetPanel: React.FC<FeatureFlagAssignmentTargetPanelProps> = ({
  assignmentIdsLoading,
  configuration,
  onAssignmentTargetChange,
  onBulkStoreAssignmentChange,
  onBulkUserAssignmentChange,
  onStoreAssignmentChange,
  onStoreSearchChange,
  onStoreStatusFilterChange,
  onUserAssignmentChange,
  storeIdsError,
  userIdsError,
  storeOptionsError,
  storeOptionsLoading,
  storeSearch,
  storeStatusFilter,
  stores,
}) => {
  const userOptions = useFeatureFlagUserOptions(configuration.assignmentTarget === 'user_wise');

  return (
    <Stack spacing={1.75}>
      <Box>
        <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 1 }}>
          Rollout Target
        </Typography>
        <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText, mb: 1 }}>
          Choose how this feature is assigned for this client.
        </Typography>
        <FeatureFlagRolloutTargetControl
          value={configuration.assignmentTarget}
          onChange={onAssignmentTargetChange}
        />
      </Box>

      {configuration.assignmentTarget === 'store_wise' ? (
        <Box>
          <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText, mt: 0.75 }}>
            Choose individual stores below.
          </Typography>
          <Box sx={{ mt: 1.25 }}>
            <FeatureFlagStoreSelector
              search={storeSearch}
              statusFilter={storeStatusFilter}
              stores={stores}
              storesError={storeOptionsError}
              storesLoading={storeOptionsLoading || assignmentIdsLoading}
              selectedStoreIds={configuration.storeIds}
              onBulkStoreAssignmentChange={onBulkStoreAssignmentChange}
              onSearchChange={onStoreSearchChange}
              onStatusFilterChange={onStoreStatusFilterChange}
              onStoreAssignmentChange={onStoreAssignmentChange}
              storeIdsError={storeIdsError}
            />
          </Box>
        </Box>
      ) : (
        <Box>
          <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText, mt: 0.75 }}>
            Choose individual users below.
          </Typography>
          <Box sx={{ mt: 1.25 }}>
            <FeatureFlagUserSelector
              search={userOptions.search}
              selectedUserIds={configuration.userIds}
              hasNextPage={userOptions.hasNextPage}
              hasPreviousPage={userOptions.hasPreviousPage}
              page={userOptions.page}
              remainingUsers={userOptions.remainingUsers}
              totalUsers={userOptions.totalUsers}
              totalPages={userOptions.totalPages}
              users={userOptions.users}
              usersError={userOptions.error}
              usersLoading={userOptions.loading || assignmentIdsLoading}
              onBulkUserAssignmentChange={onBulkUserAssignmentChange}
              onPageChange={userOptions.handlePageChange}
              onSearchChange={userOptions.handleSearchChange}
              onUserAssignmentChange={onUserAssignmentChange}
              userIdsError={userIdsError}
            />
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default React.memo(FeatureFlagAssignmentTargetPanel);
